import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, useMapEvents, useMap, LayersControl, Polyline } from 'react-leaflet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Pencil, Trash2, Search, ChevronDown, ChevronUp, Check, User, Map as MapIcon, Layers, ArrowRight, SlidersHorizontal } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useLanguage } from '../context/LanguageContext';
import { products } from '../data/mock';

// Province coordinates for map centering
const provinceCoordinates = {
  "Agrigento": [37.31, 13.58], "Alessandria": [44.91, 8.61], "Ancona": [43.62, 13.52],
  "Aosta": [45.74, 7.32], "Arezzo": [43.46, 11.88], "Ascoli Piceno": [42.85, 13.57],
  "Asti": [44.90, 8.21], "Avellino": [40.91, 14.79], "Bari": [41.12, 16.87],
  "Barletta-Andria-Trani": [41.23, 16.29], "Belluno": [46.14, 12.22], "Benevento": [41.13, 14.78],
  "Bergamo": [45.69, 9.67], "Biella": [45.56, 8.05], "Bologna": [44.49, 11.34],
  "Bolzano": [46.50, 11.35], "Brescia": [45.54, 10.21], "Brindisi": [40.63, 17.94],
  "Cagliari": [39.22, 9.12], "Caltanissetta": [37.49, 14.06], "Campobasso": [41.56, 14.66],
  "Caserta": [41.07, 14.33], "Catania": [37.50, 15.09], "Catanzaro": [38.91, 16.59],
  "Chieti": [42.35, 14.17], "Como": [45.81, 9.08], "Cosenza": [39.30, 16.25],
  "Cremona": [45.13, 10.02], "Crotone": [39.08, 17.12], "Cuneo": [44.38, 7.55],
  "Enna": [37.57, 14.27], "Fermo": [43.16, 13.72], "Ferrara": [44.84, 11.62],
  "Firenze": [43.77, 11.25], "Foggia": [41.46, 15.55], "Forlì-Cesena": [44.22, 12.04],
  "Frosinone": [41.64, 13.35], "Genova": [44.41, 8.93], "Gorizia": [45.94, 13.62],
  "Grosseto": [42.76, 11.11], "Imperia": [43.89, 8.03], "Isernia": [41.59, 14.23],
  "L'Aquila": [42.35, 13.40], "La Spezia": [44.10, 9.82], "Latina": [41.47, 12.90],
  "Lecce": [40.35, 18.17], "Lecco": [45.85, 9.39], "Livorno": [43.55, 10.31],
  "Lodi": [45.31, 9.50], "Lucca": [43.84, 10.50], "Macerata": [43.30, 13.45],
  "Mantova": [45.16, 10.79], "Massa-Carrara": [44.03, 10.14], "Matera": [40.67, 16.60],
  "Messina": [38.19, 15.55], "Milano": [45.46, 9.19], "Modena": [44.65, 10.92],
  "Monza e Brianza": [45.58, 9.27], "Napoli": [40.85, 14.27], "Novara": [45.45, 8.62],
  "Nuoro": [40.32, 9.33], "Oristano": [39.90, 8.59], "Padova": [45.41, 11.88],
  "Palermo": [38.12, 13.36], "Parma": [44.80, 10.33], "Pavia": [45.18, 9.16],
  "Perugia": [43.11, 12.39], "Pesaro e Urbino": [43.91, 12.91], "Pescara": [42.46, 14.21],
  "Piacenza": [45.05, 9.69], "Pisa": [43.72, 10.40], "Pistoia": [43.93, 10.92],
  "Pordenone": [45.96, 12.66], "Potenza": [40.64, 15.80], "Prato": [43.88, 11.10],
  "Ragusa": [36.93, 14.73], "Ravenna": [44.42, 12.20], "Reggio Calabria": [38.11, 15.65],
  "Reggio nell'Emilia": [44.70, 10.63], "Rieti": [42.40, 12.86], "Rimini": [44.06, 12.57],
  "Roma": [41.89, 12.48], "Rovigo": [45.07, 11.79], "Salerno": [40.68, 14.77],
  "Sassari": [40.73, 8.56], "Savona": [44.31, 8.48], "Siena": [43.32, 11.33],
  "Siracusa": [37.07, 15.29], "Sondrio": [46.17, 9.87], "Sud Sardegna": [39.37, 8.95],
  "Taranto": [40.47, 17.24], "Teramo": [42.66, 13.70], "Terni": [42.56, 12.64],
  "Torino": [45.07, 7.69], "Trapani": [38.02, 12.54], "Trento": [46.07, 11.12],
  "Treviso": [45.67, 12.24], "Trieste": [45.65, 13.78], "Udine": [46.06, 13.24],
  "Varese": [45.82, 8.83], "Venezia": [45.44, 12.32], "Verbano-Cusio-Ossola": [46.14, 8.27],
  "Vercelli": [45.32, 8.42], "Verona": [45.44, 10.99], "Vibo Valentia": [38.68, 16.10],
  "Vicenza": [45.55, 11.55], "Viterbo": [42.42, 12.11]
};

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const pointIcon = new L.DivIcon({
  className: 'custom-point-icon',
  html: '<div style="width: 12px; height: 12px; background: #2d7a3a; border: 2px solid white; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
  iconSize: [12, 12],
  iconAnchor: [6, 6]
});

const startPointIcon = new L.DivIcon({
  className: 'start-point-icon',
  html: '<div style="width: 18px; height: 18px; background: #FF6B35; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 6px rgba(0,0,0,0.4);"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9]
});

const calculatePolygonArea = (latlngs) => {
  if (!latlngs || latlngs.length < 3) return 0;
  const R = 6371000;
  let area = 0;
  for (let i = 0; i < latlngs.length; i++) {
    const j = (i + 1) % latlngs.length;
    const lat1 = latlngs[i].lat * Math.PI / 180;
    const lat2 = latlngs[j].lat * Math.PI / 180;
    const lng1 = latlngs[i].lng * Math.PI / 180;
    const lng2 = latlngs[j].lng * Math.PI / 180;
    area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  area = Math.abs(area * R * R / 2);
  return Math.round(area);
};

const calculatePerimeter = (latlngs) => {
  if (!latlngs || latlngs.length < 2) return 0;
  let perimeter = 0;
  for (let i = 0; i < latlngs.length; i++) {
    const j = (i + 1) % latlngs.length;
    const p1 = L.latLng(latlngs[i].lat, latlngs[i].lng);
    const p2 = L.latLng(latlngs[j].lat, latlngs[j].lng);
    perimeter += p1.distanceTo(p2);
  }
  return Math.round(perimeter);
};

// Get recommended kit for a given area
const getRecommendedKit = (areaM2) => {
  if (!areaM2 || areaM2 <= 0) return null;
  const sorted = [...products].sort((a, b) => a.coverage - b.coverage);
  return sorted.find(p => p.coverage >= areaM2) || sorted[sorted.length - 1];
};

const MapController = ({ isDrawing, searchLocation }) => {
  const map = useMap();
  useEffect(() => {
    if (searchLocation) {
      if (searchLocation.boundingbox) {
        // Use bounding box for cities/towns (shows entire area)
        const bb = searchLocation.boundingbox;
        map.fitBounds([[bb[0], bb[2]], [bb[1], bb[3]]], { maxZoom: 17, padding: [20, 20] });
      } else {
        map.setView([searchLocation.lat, searchLocation.lng], 16);
      }
    }
  }, [searchLocation, map]);
  useEffect(() => {
    const container = map.getContainer();
    container.style.cursor = isDrawing ? 'crosshair' : '';
  }, [isDrawing, map]);
  return null;
};

const DrawingHandler = ({ isDrawing, points, setPoints, onPolygonClose }) => {
  const map = useMap();
  useMapEvents({
    click(e) {
      if (isDrawing) {
        const newPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
        if (points.length >= 3) {
          const startPoint = points[0];
          const startPixel = map.latLngToContainerPoint([startPoint.lat, startPoint.lng]);
          const clickPixel = map.latLngToContainerPoint([newPoint.lat, newPoint.lng]);
          const pixelDistance = Math.sqrt(
            Math.pow(startPixel.x - clickPixel.x, 2) + Math.pow(startPixel.y - clickPixel.y, 2)
          );
          if (pixelDistance < 25) {
            onPolygonClose();
            return;
          }
        }
        setPoints(prev => [...prev, newPoint]);
      }
    }
  });
  return null;
};

const Step1ContactAndMap = ({ provinces, onSubmit }) => {
  const { t } = useLanguage();
  
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', province: '', city: '', postcode: '' });
  const [consents, setConsents] = useState({ privacy: false, marketing: false });
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityData, setCityData] = useState([]);  // full nominatim results for postcode
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const cityTimeoutRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showMap, setShowMap] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [points, setPoints] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPolygonComplete, setIsPolygonComplete] = useState(false);
  const [area, setArea] = useState(0);
  const [perimeter, setPerimeter] = useState(0);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualArea, setManualArea] = useState('');
  const [inputMode, setInputMode] = useState('map');
  const [addressQuery, setAddressQuery] = useState('');
  const [addressResults, setAddressResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [searchLocation, setSearchLocation] = useState(null);
  const [useManualFallback, setUseManualFallback] = useState(false);
  const [sliderValue, setSliderValue] = useState(500);
  const [manualInputType, setManualInputType] = useState('sqm'); // 'sqm' or 'lm'
  const [manualPerimeter, setManualPerimeter] = useState('');

  // Compute area from manual perimeter (assume square shape: area = (P/4)²)
  const perimeterToArea = (p) => Math.round((p / 4) * (p / 4));
  
  // Current effective values
  const currentArea = inputMode === 'manual' 
    ? (manualInputType === 'lm' ? perimeterToArea(parseInt(manualPerimeter) || 0) : (parseInt(manualArea) || 0))
    : area;
  const currentPerimeter = inputMode === 'manual'
    ? (manualInputType === 'lm' ? (parseInt(manualPerimeter) || 0) : (parseInt(manualArea) > 0 ? Math.round(Math.sqrt(parseInt(manualArea)) * 4) : 0))
    : perimeter;
  const recommendedKit = useMemo(() => getRecommendedKit(currentArea), [currentArea]);

  const validateField = (name, value) => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) return t.firstNameRequired;
        return '';
      case 'lastName':
        if (!value.trim()) return t.lastNameRequired;
        return '';
      case 'email':
        if (!value.trim()) return t.emailRequired;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return t.emailInvalid;
        return '';
      case 'phone':
        if (!value.trim()) return t.phoneRequired;
        if (!/^[+]?[0-9\s-]{8,}$/.test(value.replace(/\s/g, ''))) return t.phoneInvalid;
        return '';
      case 'province':
        if (!value) return t.provinceRequired;
        return '';
      case 'postcode':
        if (!value.trim()) return t.postcodeRequired;
        if (!/^[0-9]{4,6}$/.test(value.trim())) return t.postcodeInvalid;
        return '';
      default:
        return '';
    }
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, formData[name]) }));
  };

  // City autocomplete via Nominatim
  const handleCityChange = (value) => {
    handleChange('city', value);
    if (cityTimeoutRef.current) clearTimeout(cityTimeoutRef.current);
    if (value.length < 2) { setCitySuggestions([]); setCityData([]); setShowCitySuggestions(false); return; }
    cityTimeoutRef.current = setTimeout(async () => {
      try {
        const province = formData.province || '';
        const query = province ? `${value}, ${province}, Italy` : `${value}, Italy`;
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1&countrycodes=it&accept-language=it`, { headers: { 'User-Agent': 'GeyserPRO/1.0' } });
        const data = await res.json();
        const filtered = data.filter(r => r.address && (r.address.city || r.address.town || r.address.village || r.address.municipality));
        const cities = filtered.map(r => r.address.city || r.address.town || r.address.village || r.address.municipality)
          .filter((v, i, arr) => arr.indexOf(v) === i);
        setCityData(filtered);
        setCitySuggestions(cities);
        setShowCitySuggestions(cities.length > 0);
      } catch { setCitySuggestions([]); setCityData([]); }
    }, 300);
  };

  const selectCity = (city) => {
    handleChange('city', city);
    // Extract postcode from nominatim data
    const match = cityData.find(r => (r.address.city || r.address.town || r.address.village || r.address.municipality) === city);
    if (match && match.address.postcode) {
      handleChange('postcode', match.address.postcode);
    }
    setCitySuggestions([]);
    setShowCitySuggestions(false);
  };

  useEffect(() => {
    if (points.length >= 3 && inputMode === 'map') {
      setArea(calculatePolygonArea(points));
      setPerimeter(calculatePerimeter(points));
    }
  }, [points, inputMode]);

  const startDrawing = () => { setIsDrawing(true); setIsPolygonComplete(false); setPoints([]); };

  const handlePolygonClose = () => {
    if (points.length >= 3) { setIsDrawing(false); setIsPolygonComplete(true); setInputMode('map'); }
  };

  const clearDrawing = () => { setPoints([]); setIsDrawing(false); setIsPolygonComplete(false); setArea(0); setPerimeter(0); };
  const undoLastPoint = () => { setPoints(prev => prev.slice(0, -1)); };

  const searchAddress = async () => {
    if (!addressQuery.trim()) return;
    setIsSearchingAddress(true);
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addressQuery)}&limit=5&accept-language=it&countrycodes=it`);
      const data = await response.json();
      setAddressResults(data);
    } catch (error) {
      console.error('Address search error:', error);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const selectAddress = (result) => {
    setSearchLocation({ 
      lat: parseFloat(result.lat), 
      lng: parseFloat(result.lon),
      boundingbox: result.boundingbox || null
    });
    setAddressResults([]);
    setAddressQuery(result.display_name);
  };

  const handleManualAreaChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setManualArea(value);
    if (parseInt(value) > 0) {
      setInputMode('manual');
      setArea(parseInt(value));
      setPerimeter(Math.round(Math.sqrt(parseInt(value)) * 4));
      setIsPolygonComplete(true);
    } else {
      setArea(0);
      setPerimeter(0);
      setIsPolygonComplete(false);
    }
  };

  const handleManualPerimeterChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setManualPerimeter(value);
    if (parseInt(value) > 0) {
      setInputMode('manual');
      const estimatedArea = perimeterToArea(parseInt(value));
      setArea(estimatedArea);
      setPerimeter(parseInt(value));
      setManualArea(String(estimatedArea));
      setIsPolygonComplete(true);
    } else {
      setArea(0);
      setPerimeter(0);
      setIsPolygonComplete(false);
    }
  };

  // Slider-based manual input
  const handleSliderChange = (val) => {
    setSliderValue(val);
    setManualArea(String(val));
    setInputMode('manual');
    setArea(val);
    setIsPolygonComplete(true);
  };

  const hasValidArea = (inputMode === 'manual' && (parseInt(manualArea) > 0 || parseInt(manualPerimeter) > 0)) || 
                       (inputMode === 'map' && area > 0 && isPolygonComplete);
  
  const isFormValid = () => {
    const newErrors = {
      firstName: validateField('firstName', formData.firstName),
      lastName: validateField('lastName', formData.lastName),
      email: validateField('email', formData.email),
      phone: validateField('phone', formData.phone),
      province: validateField('province', formData.province),
      postcode: validateField('postcode', formData.postcode)
    };
    return !Object.values(newErrors).some(error => error !== '');
  };

  const canProceed = hasValidArea && showContactForm && isFormValid() && consents.privacy;

  const handleSubmit = () => {
    const newErrors = {
      firstName: validateField('firstName', formData.firstName),
      lastName: validateField('lastName', formData.lastName),
      email: validateField('email', formData.email),
      phone: validateField('phone', formData.phone),
      province: validateField('province', formData.province),
      postcode: validateField('postcode', formData.postcode)
    };
    setErrors(newErrors);
    setTouched({ firstName: true, lastName: true, email: true, phone: true, province: true, postcode: true });
    if (Object.values(newErrors).some(error => error !== '')) return;
    if (!hasValidArea) return;
    if (!consents.privacy) return;
    const finalArea = inputMode === 'manual' ? currentArea : area;
    const finalPerimeter = inputMode === 'manual' ? currentPerimeter : perimeter;
    onSubmit({
      contactData: { firstName: formData.firstName, lastName: formData.lastName, email: formData.email, phone: formData.phone, province: formData.province, city: formData.city, postcode: formData.postcode },
      gardenData: {
        inputMode, areaM2: finalArea, perimeterM: finalPerimeter,
        addressQuery: addressQuery || null,
        pinLat: searchLocation?.lat || null, pinLng: searchLocation?.lng || null,
        mapDrawingData: inputMode === 'map' ? points : null
      },
      consents: { privacy: consents.privacy, marketing: consents.marketing }
    });
  };

  const contactFormRef = useRef(null);
  const continueToContact = () => {
    setShowManualInput(false);
    setShowContactForm(true);
    // Auto-scroll to contact form on mobile
    setTimeout(() => {
      if (contactFormRef.current) {
        contactFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Sticky bottom action bar — one button visible at all times that advances the funnel.
  // Hidden only during map drawing (map has its own controls).
  const stickyConfig = (() => {
    if (showMap && !isPolygonComplete && !useManualFallback) return null;
    if (hasValidArea && showContactForm) {
      return {
        label: t.continueToKit,
        onClick: handleSubmit,
        disabled: !canProceed,
        hint: canProceed ? null : 'Compila tutti i campi obbligatori sopra',
        color: 'orange',
        testid: 'sticky-submit-step1-btn',
      };
    }
    if (hasValidArea && !showContactForm) {
      return {
        label: 'Continua',
        onClick: continueToContact,
        disabled: false,
        color: 'orange',
        testid: 'sticky-continue-to-contact-btn',
      };
    }
    if (useManualFallback) {
      return {
        label: 'Continua',
        onClick: continueToContact,
        disabled: !hasValidArea,
        hint: hasValidArea ? null : 'Inserisci i metri quadri sopra per continuare',
        color: 'orange',
        testid: 'sticky-continue-manual-btn',
      };
    }
    return {
      label: t.startDrawing,
      onClick: () => setShowMap(true),
      disabled: false,
      color: 'green',
      testid: 'sticky-start-drawing-btn',
    };
  })();

  return (
    <div className="max-w-4xl mx-auto pb-28">
      {/* Instructions + Start (when no map shown and no area) */}
      {!showMap && (!hasValidArea || showManualInput) && !useManualFallback && (
        <div className="mb-6">
          <div className="bg-stocker-green/5 border border-stocker-green/20 rounded-xl p-5 mb-5">
            <div className="flex flex-col md:flex-row items-center gap-5">
              {/* Map illustration */}
              <div className="w-full md:w-1/3 flex-shrink-0">
                <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-200">
                  <svg viewBox="0 0 200 150" className="w-full h-auto">
                    <rect x="10" y="10" width="180" height="130" rx="8" fill="#E8F5E9" stroke="#2d7a3a" strokeWidth="2"/>
                    <line x1="10" y1="50" x2="190" y2="50" stroke="#C8E6C9" strokeWidth="1"/>
                    <line x1="10" y1="90" x2="190" y2="90" stroke="#C8E6C9" strokeWidth="1"/>
                    <line x1="70" y1="10" x2="70" y2="140" stroke="#C8E6C9" strokeWidth="1"/>
                    <line x1="130" y1="10" x2="130" y2="140" stroke="#C8E6C9" strokeWidth="1"/>
                    <polygon points="40,100 60,40 140,35 160,80 130,120 70,115" fill="#2d7a3a" fillOpacity="0.3" stroke="#2d7a3a" strokeWidth="3"/>
                    <circle cx="40" cy="100" r="8" fill="#FF6B35" stroke="white" strokeWidth="2"/>
                    <circle cx="60" cy="40" r="6" fill="#2d7a3a" stroke="white" strokeWidth="2"/>
                    <circle cx="140" cy="35" r="6" fill="#2d7a3a" stroke="white" strokeWidth="2"/>
                    <circle cx="160" cy="80" r="6" fill="#2d7a3a" stroke="white" strokeWidth="2"/>
                    <circle cx="130" cy="120" r="6" fill="#2d7a3a" stroke="white" strokeWidth="2"/>
                    <circle cx="70" cy="115" r="6" fill="#2d7a3a" stroke="white" strokeWidth="2"/>
                    <rect x="75" y="65" width="50" height="24" rx="4" fill="white" stroke="#2d7a3a" strokeWidth="1"/>
                    <text x="100" y="82" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#2d7a3a">250 m²</text>
                  </svg>
                </div>
              </div>
              {/* Instructions */}
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2 justify-center md:justify-start">
                  <MapIcon className="w-5 h-5 text-stocker-green" />
                  {t.howItWorks}
                </h3>
                <ol className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-stocker-green text-white text-xs flex items-center justify-center font-bold flex-shrink-0">1</span>
                    {t.drawInstructions1}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-stocker-green text-white text-xs flex items-center justify-center font-bold flex-shrink-0">2</span>
                    {t.drawInstructions2}
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-stocker-green text-white text-xs flex items-center justify-center font-bold flex-shrink-0">3</span>
                    Clicca sul punto di partenza per chiudere il perimetro
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col items-center gap-3">
            {!hasValidArea && (
              <Button data-testid="start-drawing-btn" onClick={() => setShowMap(true)} className="min-h-[48px] px-8 bg-stocker-green hover:bg-stocker-green-dark text-white font-bold uppercase tracking-wide">
                <Pencil className="w-5 h-5 mr-2" />
                {t.startDrawing}
              </Button>
            )}
            <button onClick={() => setShowManualInput(!showManualInput)} className="text-sm text-gray-500 hover:text-stocker-green flex items-center gap-1 underline">
              {showManualInput ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {t.manualInput}
            </button>
          </div>

          {showManualInput && (
            <div className="mt-4 p-4 bg-white rounded-lg max-w-sm mx-auto border-2 border-stocker-green/30">
              {/* Toggle m² / Laufmeter */}
              <div className="flex gap-1 mb-3 bg-gray-100 rounded-lg p-1">
                <button onClick={() => setManualInputType('sqm')} className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors ${manualInputType === 'sqm' ? 'bg-white text-stocker-green shadow-sm' : 'text-gray-500'}`}>
                  Metri quadri (m&sup2;)
                </button>
                <button onClick={() => setManualInputType('lm')} className={`flex-1 text-xs font-semibold py-2 rounded-md transition-colors ${manualInputType === 'lm' ? 'bg-white text-stocker-green shadow-sm' : 'text-gray-500'}`}>
                  Metri lineari (m)
                </button>
              </div>
                    {manualInputType === 'sqm' ? (
                      <div className="flex gap-2 items-center">
                        <Input type="text" inputMode="numeric" pattern="[0-9]*" value={manualArea} onChange={handleManualAreaChange} placeholder={t.areaPlaceholder} className="flex-1 h-12 text-lg font-bold text-center" autoFocus />
                        <span className="text-gray-500 font-medium text-lg">m&sup2;</span>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center">
                        <Input type="text" inputMode="numeric" pattern="[0-9]*" value={manualPerimeter} onChange={handleManualPerimeterChange} placeholder="Es. 80" className="flex-1 h-12 text-lg font-bold text-center" autoFocus />
                        <span className="text-gray-500 font-medium text-lg">m</span>
                      </div>
                    )}
                    {currentArea > 0 && (
                      <div className="mt-3 text-center">
                        <p className="text-sm text-stocker-green font-bold mb-1">
                          ~{currentArea.toLocaleString()} m&sup2; &middot; ~{currentPerimeter.toLocaleString()} m
                        </p>
                        {recommendedKit && (
                          <p data-testid="kit-recommendation-manual" className="text-xs text-gray-600 mb-2">
                            Per ~{currentArea.toLocaleString()} m&sup2; ti consigliamo il <strong className="text-stocker-green">{recommendedKit.name}</strong>
                          </p>
                        )}
                        <Button onClick={continueToContact} className="w-full min-h-[48px] bg-stocker-orange hover:bg-stocker-orange-hover text-white font-bold uppercase">
                          Continua <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </div>
                    )}
            </div>
          )}
        </div>
      )}

      {/* Map Section */}
      {showMap && !isPolygonComplete && !useManualFallback && (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <MapIcon className="w-5 h-5 text-stocker-green" />
            {t.drawGarden}
          </h3>

          {/* Mobile Fallback Toggle */}
          <button
            data-testid="manual-fallback-toggle"
            onClick={() => { setUseManualFallback(true); setShowMap(false); }}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-stocker-green mb-4 underline transition-colors"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Non riesci a disegnare? Inserisci i metri quadri manualmente
          </button>

          {/* Address Search */}
          <div className="mb-4">
            <Label className="text-gray-700 font-medium mb-2 block">
              <Search className="w-4 h-4 inline mr-1" />
              {t.searchAddress}
            </Label>
            <div className="flex gap-2">
              <Input type="text" value={addressQuery} onChange={(e) => setAddressQuery(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && searchAddress()} placeholder={t.searchPlaceholder} className="flex-1 h-11" />
              <Button onClick={searchAddress} disabled={isSearchingAddress} className="h-11 px-4 bg-stocker-green hover:bg-stocker-green-dark">
                <Search className="w-4 h-4" />
              </Button>
            </div>
            {addressResults.length > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto border rounded-md bg-white shadow-lg z-50 relative">
                {addressResults.map((result, idx) => (
                  <button key={`addr-${result.place_id || idx}`} onClick={() => selectAddress(result)} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 border-b last:border-b-0">
                    {result.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="rounded-xl overflow-hidden shadow-lg border-2 border-gray-200" style={{ height: '400px' }}>
                <MapContainer center={[41.89, 12.48]} zoom={6} maxZoom={22} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name={t.streetMap}>
                      <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxNativeZoom={19} maxZoom={22} />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name={t.satellite}>
                      <TileLayer attribution='&copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxNativeZoom={19} maxZoom={22} />
                    </LayersControl.BaseLayer>
                    <LayersControl.BaseLayer name={t.hybrid}>
                      <TileLayer attribution='&copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxNativeZoom={19} maxZoom={22} />
                    </LayersControl.BaseLayer>
                  </LayersControl>
                  <MapController isDrawing={isDrawing} searchLocation={searchLocation} />
                  <DrawingHandler isDrawing={isDrawing} points={points} setPoints={setPoints} onPolygonClose={handlePolygonClose} />
                  {points.length >= 2 && !isPolygonComplete && (
                    <Polyline positions={points.map(p => [p.lat, p.lng])} pathOptions={{ color: '#2d7a3a', weight: 3, dashArray: '5, 10' }} />
                  )}
                  {points.map((point, idx) => (
                    <Marker key={`point-${point.lat}-${point.lng}`} position={[point.lat, point.lng]} icon={idx === 0 ? startPointIcon : pointIcon} />
                  ))}
                  {points.length >= 3 && isPolygonComplete && (
                    <Polygon positions={points.map(p => [p.lat, p.lng])} pathOptions={{ color: '#2d7a3a', fillColor: '#2d7a3a', fillOpacity: 0.3, weight: 3 }} />
                  )}
                </MapContainer>
              </div>
              <p className="text-center text-xs text-gray-500 mt-2 flex items-center justify-center gap-2">
                <Layers className="w-3 h-3" /> {t.mapLayerHint}
              </p>
            </div>

            <div className="lg:col-span-1">
              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  {!isDrawing && !isPolygonComplete && (
                    <Button data-testid="draw-btn" onClick={startDrawing} className="min-h-[48px] bg-stocker-green hover:bg-stocker-green-dark text-white font-bold">
                      <Pencil className="w-4 h-4 mr-2" /> {t.draw}
                    </Button>
                  )}
                  {isDrawing && (
                    <Button onClick={undoLastPoint} variant="outline" className="min-h-[48px] border-2" disabled={points.length === 0}>
                      {t.undo}
                    </Button>
                  )}
                  {points.length > 0 && (
                    <Button onClick={clearDrawing} variant="outline" className="min-h-[48px] border-2 border-red-300 text-red-600 hover:bg-red-50">
                      <Trash2 className="w-4 h-4 mr-2" /> {t.delete}
                    </Button>
                  )}
                </div>

                {isDrawing && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                    {points.length < 3 ? (
                      <>{t.clickOnMap} ({points.length} {t.points})</>
                    ) : (
                      <span className="font-medium">Clicca sul punto arancione per chiudere il perimetro</span>
                    )}
                  </div>
                )}

                <div className={`rounded-lg p-4 text-center ${area > 0 ? 'bg-stocker-green/10 border-2 border-stocker-green' : 'bg-gray-100 border-2 border-dashed border-gray-300'}`}>
                  {area > 0 ? (
                    <>
                      <p className="text-sm text-gray-600">{t.area}</p>
                      <p className="text-3xl font-extrabold text-stocker-green">{area.toLocaleString()} m&sup2;</p>
                      {perimeter > 0 && (
                        <p className="text-lg font-bold text-stocker-green/80 mt-1">{perimeter.toLocaleString()} metri lineari</p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500 text-sm">{t.noAreaYet}</p>
                  )}
                </div>

                {/* Hint for address search */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                  <p className="text-sm font-semibold text-amber-800 mb-1">Strada non trovata?</p>
                  <p className="text-xs text-amber-700">Cerca una via principale vicina o il nome della citt&agrave; e naviga manualmente fino al punto desiderato.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Manual Fallback - Slider + Input */}
      {useManualFallback && !showContactForm && (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-stocker-green" />
            Inserisci le misure manualmente
          </h3>
          <button onClick={() => { setUseManualFallback(false); setShowMap(true); }} className="text-sm text-gray-500 hover:text-stocker-green underline mb-4 block">
            Torna alla mappa
          </button>
          
          <div className="space-y-6">
            {/* Toggle m² / Laufmeter */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              <button data-testid="toggle-sqm" onClick={() => setManualInputType('sqm')} className={`flex-1 text-sm font-semibold py-2.5 rounded-md transition-colors ${manualInputType === 'sqm' ? 'bg-white text-stocker-green shadow-sm' : 'text-gray-500'}`}>
                Metri quadri (m&sup2;)
              </button>
              <button data-testid="toggle-lm" onClick={() => setManualInputType('lm')} className={`flex-1 text-sm font-semibold py-2.5 rounded-md transition-colors ${manualInputType === 'lm' ? 'bg-white text-stocker-green shadow-sm' : 'text-gray-500'}`}>
                Metri lineari (m)
              </button>
            </div>

            {manualInputType === 'sqm' ? (
              <>
                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">Area del giardino (m&sup2;)</Label>
                  <Input
                    data-testid="manual-area-input"
                    type="text" inputMode="numeric" pattern="[0-9]*"
                    value={manualArea}
                    onChange={handleManualAreaChange}
                    placeholder="Es. 500"
                    className="h-14 text-2xl font-bold text-center"
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>100 m&sup2;</span>
                    <span>5.000 m&sup2;</span>
                  </div>
                  <input
                    data-testid="area-slider"
                    type="range" min="100" max="5000" step="50"
                    value={sliderValue}
                    onChange={(e) => handleSliderChange(parseInt(e.target.value))}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-stocker-green"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label className="text-gray-700 font-medium mb-2 block">Perimetro del giardino (metri lineari)</Label>
                  <Input
                    data-testid="manual-perimeter-input"
                    type="text" inputMode="numeric" pattern="[0-9]*"
                    value={manualPerimeter}
                    onChange={handleManualPerimeterChange}
                    placeholder="Es. 80"
                    className="h-14 text-2xl font-bold text-center"
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs text-gray-400 mb-1">
                    <span>20 m</span>
                    <span>500 m</span>
                  </div>
                  <input
                    data-testid="perimeter-slider"
                    type="range" min="20" max="500" step="5"
                    value={parseInt(manualPerimeter) || 80}
                    onChange={(e) => { const v = parseInt(e.target.value); setManualPerimeter(String(v)); handleManualPerimeterChange({ target: { value: String(v) }}); }}
                    className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-stocker-green"
                  />
                </div>
              </>
            )}

            {/* Show both values */}
            {currentArea > 0 && (
              <div className="text-center">
                <p className="text-3xl font-extrabold text-stocker-green">
                  ~{currentArea.toLocaleString()} m&sup2;
                </p>
                <p className="text-lg font-bold text-stocker-green/80 mt-1">
                  ~{currentPerimeter.toLocaleString()} metri lineari
                </p>
              </div>
            )}

            {currentArea > 0 && recommendedKit && (
              <div data-testid="kit-recommendation-slider" className="bg-stocker-green/10 border border-stocker-green/30 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600">
                  Per ~{currentArea.toLocaleString()} m&sup2; ti consigliamo il <strong className="text-stocker-green">{recommendedKit.name}</strong>
                </p>
              </div>
            )}

            {currentArea > 0 && (
              <Button data-testid="continue-from-manual-btn" onClick={continueToContact} className="w-full min-h-[48px] bg-stocker-orange hover:bg-stocker-orange-hover text-white font-bold uppercase">
                Continua <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Area Summary (after area calculated via MAP, before contact) */}
      {hasValidArea && !showContactForm && !showManualInput && !useManualFallback && (
        <div className="bg-stocker-green/10 border-2 border-stocker-green rounded-xl p-6 mb-6 text-center">
          <p className="text-sm text-gray-600 mb-1">{t.yourGardenArea}</p>
          <p className="text-4xl font-extrabold text-stocker-green mb-1">
            ~{currentArea.toLocaleString()} m&sup2;
          </p>
          {currentPerimeter > 0 && (
            <p className="text-lg font-bold text-stocker-green/80 mb-2">~{currentPerimeter.toLocaleString()} metri lineari</p>
          )}
          {/* Kit recommendation */}
          {recommendedKit && (
            <p data-testid="kit-recommendation-map" className="text-sm text-gray-700 mb-4">
              Per ~{currentArea.toLocaleString()} m&sup2; ti consigliamo il <strong className="text-stocker-green">{recommendedKit.name}</strong>
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => { setShowMap(true); setIsPolygonComplete(false); setPoints([]); setArea(0); setPerimeter(0); if (inputMode === 'manual') { setManualArea(''); setInputMode('map'); } }} variant="outline" className="min-h-[48px] border-stocker-green text-stocker-green hover:bg-stocker-green/10">
              Modifica area
            </Button>
            <Button data-testid="continue-to-contact-btn" onClick={continueToContact} className="min-h-[48px] px-8 bg-stocker-orange hover:bg-stocker-orange-hover text-white font-bold uppercase">
              Continua <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* SECTION 2: CONTACT DATA */}
      {hasValidArea && showContactForm && (
        <>
          {/* Compact area summary — small on mobile, bigger on desktop */}
          <div ref={contactFormRef} className="flex items-center justify-between bg-stocker-green/10 border border-stocker-green/30 rounded-lg px-4 py-2.5 mb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-stocker-green">~{currentArea.toLocaleString()} m²</span>
              {currentPerimeter > 0 && <span className="text-xs text-stocker-green/70">· ~{currentPerimeter.toLocaleString()} m</span>}
              {recommendedKit && <span className="text-xs text-gray-500 hidden md:inline">· {recommendedKit.name}</span>}
            </div>
            <button onClick={() => setShowContactForm(false)} className="text-xs text-stocker-green underline">Modifica</button>
          </div>

          <div className="text-center mb-5">
            <h2 className="text-lg md:text-2xl font-bold text-gray-900 mb-1">{t.contactTitle}</h2>
            <p className="text-gray-600 text-xs md:text-sm">{t.contactSubtitle}</p>
          </div>

          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-stocker-green" />
              {t.contactData}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-gray-700 font-medium">{t.firstName} *</Label>
                <Input data-testid="input-firstname" id="firstName" type="text" value={formData.firstName} onChange={(e) => handleChange('firstName', e.target.value)} onBlur={() => handleBlur('firstName')} placeholder={t.firstNamePlaceholder} className={`h-12 ${errors.firstName && touched.firstName ? 'border-red-500' : ''}`} />
                {errors.firstName && touched.firstName && <p className="text-red-500 text-sm">{errors.firstName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-gray-700 font-medium">{t.lastName} *</Label>
                <Input data-testid="input-lastname" id="lastName" type="text" value={formData.lastName} onChange={(e) => handleChange('lastName', e.target.value)} onBlur={() => handleBlur('lastName')} placeholder={t.lastNamePlaceholder} className={`h-12 ${errors.lastName && touched.lastName ? 'border-red-500' : ''}`} />
                {errors.lastName && touched.lastName && <p className="text-red-500 text-sm">{errors.lastName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-medium">{t.email} *</Label>
                <Input data-testid="input-email" id="email" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} onBlur={() => handleBlur('email')} placeholder={t.emailPlaceholder} className={`h-12 ${errors.email && touched.email ? 'border-red-500' : ''}`} />
                {errors.email && touched.email && <p className="text-red-500 text-sm">{errors.email}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-700 font-medium">{t.phone} *</Label>
                <Input data-testid="input-phone" id="phone" type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} onBlur={() => handleBlur('phone')} placeholder={t.phonePlaceholder} className={`h-12 ${errors.phone && touched.phone ? 'border-red-500' : ''}`} />
                {errors.phone && touched.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="province" className="text-gray-700 font-medium">{t.province} *</Label>
                <Select value={formData.province} onValueChange={(value) => { handleChange('province', value); setTouched(prev => ({ ...prev, province: true })); }}>
                  <SelectTrigger data-testid="select-province" className={`h-12 ${errors.province && touched.province ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder={t.provincePlaceholder} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {provinces.map((province) => (
                      <SelectItem key={province} value={province}>{province}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.province && touched.province && <p className="text-red-500 text-sm">{errors.province}</p>}
              </div>
              <div className="space-y-2 relative">
                <Label htmlFor="city" className="text-gray-700 font-medium">Città / Comune</Label>
                <Input data-testid="input-city" id="city" value={formData.city} onChange={(e) => handleCityChange(e.target.value)} onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)} placeholder="Es. Pisa" className="h-12" autoComplete="off" />
                {showCitySuggestions && citySuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-40 overflow-y-auto">
                    {citySuggestions.map((city, idx) => (
                      <button key={`city-${city}-${idx}`} type="button" onMouseDown={() => selectCity(city)} className="w-full text-left px-3 py-2.5 text-sm hover:bg-stocker-green/10 border-b border-gray-100 last:border-0">
                        {city}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode" className="text-gray-700 font-medium">CAP *</Label>
                <Input data-testid="input-postcode" id="postcode" value={formData.postcode} onChange={(e) => handleChange('postcode', e.target.value)} onBlur={() => handleBlur('postcode')} placeholder="Es. 56100" maxLength={10} className={`h-12 ${errors.postcode && touched.postcode ? 'border-red-500' : ''}`} />
                {errors.postcode && touched.postcode && <p className="text-red-500 text-sm">{errors.postcode}</p>}
              </div>
            </div>
            {/* Consents */}
            <div className="mt-6 pt-5 border-t border-gray-200 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  data-testid="consent-privacy-checkbox"
                  type="checkbox"
                  checked={consents.privacy}
                  onChange={(e) => setConsents(prev => ({ ...prev, privacy: e.target.checked }))}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-stocker-green focus:ring-stocker-green cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-gray-700 leading-snug">
                  Ho letto e confermo la{' '}
                  <a
                    href="https://www.stockergarden.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-stocker-green underline hover:text-stocker-green-dark font-medium"
                    data-testid="privacy-policy-link"
                  >
                    privacy policy
                  </a>
                  {' '}per il trattamento dei dati personali. <span className="text-red-500">*</span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  data-testid="consent-marketing-checkbox"
                  type="checkbox"
                  checked={consents.marketing}
                  onChange={(e) => setConsents(prev => ({ ...prev, marketing: e.target.checked }))}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-stocker-green focus:ring-stocker-green cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-gray-700 leading-snug">
                  Sì, voglio ricevere la newsletter con consigli pratici e suggerimenti utili per tenere lontane le zanzare.
                </span>
              </label>
            </div>
          </div>

          <Button data-testid="submit-step1-btn" onClick={handleSubmit} disabled={!canProceed} className="w-full min-h-[48px] bg-stocker-orange hover:bg-stocker-orange-hover text-white font-bold text-sm uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed">
            {t.continueToKit}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </>
      )}

      {/* Sticky bottom action bar — always visible CTA to advance the funnel */}
      {stickyConfig && (
        <div data-testid="step1-sticky-bar" className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-3 md:px-4 py-2.5 md:py-3">
          <div className="max-w-2xl mx-auto">
            {stickyConfig.hint && (
              <p className="text-center text-[11px] md:text-xs text-gray-500 mb-1.5">{stickyConfig.hint}</p>
            )}
            <Button
              data-testid={stickyConfig.testid}
              onClick={stickyConfig.onClick}
              disabled={stickyConfig.disabled}
              className={`w-full min-h-[44px] md:min-h-[48px] text-white font-bold text-sm uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed ${stickyConfig.color === 'green' ? 'bg-stocker-green hover:bg-stocker-green-dark' : 'bg-stocker-orange hover:bg-stocker-orange-hover'}`}
            >
              {stickyConfig.color === 'green' && <Pencil className="w-4 h-4 mr-2" />}
              {stickyConfig.label}
              {stickyConfig.color !== 'green' && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step1ContactAndMap;
